import React, { Suspense, useEffect } from 'react'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import navigate from 'utils/navigate'
import { useScrollAfterLoad, useQueryParam } from '@seasonedsoftware/utils'

import useRedirectToSurvey from 'utils/useRedirectToSurvey'
import useSendPageViewToGTM from 'utils/useSendPageViewToGTM'
import useRegisterCampaign from 'utils/useRegisterCampaign'
import MediavineBlocker from 'ui/MediavineBlocker'
import CanonicalTag from 'ui/CanonicalTag'
import canonicalPath from 'utils/canonicalPath'
import Layout from 'ui/Layout'
import Loading from 'ui/Loading'
import Auth from 'auth/Auth'

export const Root = ({ children }) => children

export const redirectUnauthorizedUser = path => async () => {
  await navigate(path, { replace: true })
}

export const adminRouteUnauthorize = currentUser => {
  const type = get(currentUser, 'userType')
  return type !== 'admin' && type !== 'operator'
}

export const redirectUserToHome = pathname => currentUser => {
  navigate(currentUser ? '/' : `/sign-in/?redirect_to=${pathname}`, {
    replace: true,
  })
}

export const AdminRoute = props => {
  const { pathname } = props.location

  return (
    <Route
      {...props}
      authorize
      unauthorize={adminRouteUnauthorize}
      unauthorized={redirectUserToHome(pathname)}
    />
  )
}

export const Route = ({
  Component,
  authorize,
  unauthorize,
  unauthorizeMessage,
  authRedirect,
  disableRoutingScroll,
  LayoutComponent = Layout,
  ...props
}) => {
  const { location } = props
  const { pathname, hash } = location
  useScrollAfterLoad(!disableRoutingScroll && !hash && pathname)
  useSendPageViewToGTM({ pathname })
  useRedirectToSurvey()

  const {
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_content: utmContent,
  } = useQueryParam([
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ])

  useRegisterCampaign({
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
  })

  useEffect(() => {
    const pathWithTrailingSlash = canonicalPath(location)
    pathname !== pathWithTrailingSlash && navigate(pathWithTrailingSlash)
  }, [pathname, location])

  const path = authRedirect || `/sign-in/?redirect_to=${pathname}`

  return (
    <LayoutComponent {...props}>
      <MediavineBlocker />
      <CanonicalTag {...props.location} />
      <Suspense fallback={<Loading />}>
        {authorize ? (
          <Auth
            authorizing={<Loading />}
            unauthorize={unauthorize}
            unauthorized={redirectUnauthorizedUser(path)}
            Component={Component}
            {...props}
          />
        ) : (
          <Component {...props} />
        )}
      </Suspense>
    </LayoutComponent>
  )
}

Route.propTypes = {
  authRedirect: PropTypes.string,
  Component: PropTypes.elementType.isRequired,
  LayoutComponent: PropTypes.elementType,
  unauthorize: PropTypes.func,
  unauthorizeMessage: PropTypes.string,
  authorize: PropTypes.bool,
  disableRoutingScroll: PropTypes.bool,
  location: PropTypes.object,
}
